import { useState, useEffect, useMemo } from "react";
import {
  Bell,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  Mail,
  Webhook,
  Smartphone,
  RefreshCw,
  Settings,
  BarChart3,
  Send,
  Users,
  Server,
  ShieldAlert,
} from "lucide-react";
import { Card, CardHeader, CardBody } from "@/components/ui/Card";
import { StatCard } from "@/components/ui/StatCard";
import adminService from "@/services/admin-service";
import { normalizePercent } from "@/lib/number";
import { useAuthStore } from "@/stores/auth-store";
import { useNavigate } from "@tanstack/react-router";
import type {
  NotificationHealth,
  NotificationSystemStatus,
  NotificationStats,
  Notification,
  DeliveryLog,
  EventType,
  Subscription,
  SubscriptionStats,
} from "@/types/admin";

const TabButton = ({
  label,
  icon: Icon,
  isActive,
  onClick,
}: {
  id: string;
  label: string;
  icon: any;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`relative flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 ${
      isActive
        ? "bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg"
        : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
    }`}
  >
    <Icon className="h-4 w-4" />
    {label}
    {isActive && (
      <div className="absolute -bottom-1 left-1/2 h-1 w-8 -translate-x-1/2 rounded-full bg-white/30" />
    )}
  </button>
);

const formatShortId = (value?: string | null) => {
  if (!value || typeof value !== "string") return "—";
  return value.length > 8 ? `${value.slice(0, 8)}...` : value;
};

const normalizeDateString = (value: string) => {
  // Normalize common backend timestamp formats for JS Date parsing:
  // - Accept "2026-01-07T11:16:25.600469" (6-digit micros) by truncating to millis
  // - Accept space-separated timestamps by converting to 'T'
  // - If no timezone is provided, assume UTC and append 'Z'
  const raw = value.trim().replace(" ", "T");
  const m = raw.match(
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(\.(\d+))?(Z|[+-]\d{2}:\d{2})?$/,
  );
  if (!m) return value;

  const base = m[1];
  const fracDigits = m[3];
  const tz = m[4];

  let frac = "";
  if (fracDigits) {
    // JS Date supports milliseconds (3 digits). Truncate/pad as needed.
    const ms = (fracDigits + "000").slice(0, 3);
    frac = `.${ms}`;
  }

  const tzPart = tz || "Z";
  return `${base}${frac}${tzPart}`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const normalized = normalizeDateString(value);
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const formatDurationMs = (value: unknown) => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? `${Math.round(n)}ms` : "—";
};

type SortDir = "asc" | "desc";
type SortState<T extends string> = { key: T; dir: SortDir } | null;

const getSortIndicator = (active: boolean, dir?: SortDir) => {
  if (!active || !dir) return "";
  return dir === "asc" ? " ▲" : " ▼";
};

const compareValues = (a: any, b: any) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  // Dates (ISO strings)
  if (typeof a === "string" && typeof b === "string") {
    const da = Date.parse(a);
    const db = Date.parse(b);
    if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
  }

  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
};

function sortBy<T>(rows: T[], selector: (row: T) => any, dir: SortDir) {
  const mult = dir === "asc" ? 1 : -1;
  return [...rows].sort(
    (ra, rb) => mult * compareValues(selector(ra), selector(rb)),
  );
}

function paginate<T>(rows: T[], page: number, pageSize: number) {
  const safePageSize = Math.max(1, pageSize);
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / safePageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * safePageSize;
  const end = start + safePageSize;
  return {
    page: safePage,
    pageSize: safePageSize,
    total,
    totalPages,
    start,
    end: Math.min(end, total),
    rows: rows.slice(start, end),
  };
}

const PaginationControls = ({
  page,
  totalPages,
  total,
  start,
  end,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  start: number;
  end: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) => (
  <div className="flex flex-col gap-2 border-t border-gray-200 bg-gray-50/50 px-6 py-3 dark:border-gray-700 dark:bg-gray-800/30 sm:flex-row sm:items-center sm:justify-between">
    <div className="text-sm text-gray-600 dark:text-gray-400">
      Showing{" "}
      <span className="font-medium text-gray-900 dark:text-white">
        {total === 0 ? 0 : start + 1}
      </span>{" "}
      to{" "}
      <span className="font-medium text-gray-900 dark:text-white">{end}</span>{" "}
      of{" "}
      <span className="font-medium text-gray-900 dark:text-white">{total}</span>
    </div>
    <div className="flex items-center gap-3">
      <label className="text-sm text-gray-600 dark:text-gray-400">Rows</label>
      <select
        value={pageSize}
        onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
        className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
      >
        {[10, 25, 50, 100].map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
      >
        Prev
      </button>
      <div className="text-sm text-gray-600 dark:text-gray-400">
        Page{" "}
        <span className="font-medium text-gray-900 dark:text-white">
          {page}
        </span>{" "}
        / {totalPages}
      </div>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
      >
        Next
      </button>
    </div>
  </div>
);

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { isOrgAdmin } = useAuthStore();

  // Check if user is org admin or super admin (isOrgAdmin returns true for both)
  const canAccess = isOrgAdmin();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<NotificationHealth | null>(null);
  const [systemStatus, setSystemStatus] =
    useState<NotificationSystemStatus | null>(null);
  const [stats, setStats] = useState<NotificationStats | null>(null);

  // Failed Notifications state
  const [failedNotifications, setFailedNotifications] = useState<
    Notification[]
  >([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState<string | null>(
    null,
  );
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState<string>("");
  const [detailsPayload, setDetailsPayload] = useState<any>(null);
  const [retryConfig, setRetryConfig] = useState({
    max_attempts: 3,
    delay_seconds: 30,
  });

  // Delivery Logs state
  const [deliveryLogs, setDeliveryLogs] = useState<DeliveryLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [logsQuery, setLogsQuery] = useState({
    hours: 24,
    limit: 100,
    provider: "",
    status: "",
    minDurationMs: "",
    maxDurationMs: "",
  });

  // Event Types state
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const [eventTypesError, setEventTypesError] = useState<string | null>(null);
  const [eventTypeModalOpen, setEventTypeModalOpen] = useState(false);
  const [editingEventType, setEditingEventType] = useState<EventType | null>(
    null,
  );
  const [eventTypeCategories, setEventTypeCategories] = useState<string[]>([]);

  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState<string | null>(
    null,
  );
  const [subscriptionStats, setSubscriptionStats] =
    useState<SubscriptionStats | null>(null);
  const [subscriptionsView, setSubscriptionsView] = useState<"active" | "all">(
    "active",
  );

  // Event Publisher state
  const [eventForm, setEventForm] = useState({
    event_type: "",
    user_id: "",
    priority: "normal" as "low" | "normal" | "high" | "critical",
    payload: "{}",
    metadata: "{}",
  });
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishSuccess, setPublishSuccess] = useState<string | null>(null);
  const [recentEvents, setRecentEvents] = useState<any[]>([]);

  // Sorting state (per-table)
  const [failedSort, setFailedSort] = useState<
    SortState<
      "id" | "event_type" | "delivery_channel" | "user_id" | "created_at"
    >
  >({ key: "created_at", dir: "desc" });
  const [logsSort, setLogsSort] = useState<
    SortState<
      "created_at" | "notification_id" | "provider" | "status" | "duration_ms"
    >
  >({ key: "created_at", dir: "desc" });
  const [eventTypesSort, setEventTypesSort] = useState<
    SortState<
      "name" | "display_name" | "category" | "is_active" | "schema_version"
    >
  >({ key: "name", dir: "asc" });
  const [subsSort, setSubsSort] = useState<
    SortState<
      | "id"
      | "user_id"
      | "event_type"
      | "delivery_channel"
      | "is_active"
      | "created_at"
    >
  >({ key: "created_at", dir: "desc" });

  // Pagination state (per-table)
  const [failedPage, setFailedPage] = useState(1);
  const [failedPageSize, setFailedPageSize] = useState(25);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(25);
  const [eventTypesPage, setEventTypesPage] = useState(1);
  const [eventTypesPageSize, setEventTypesPageSize] = useState(25);
  const [subsPage, setSubsPage] = useState(1);
  const [subsPageSize, setSubsPageSize] = useState(25);

  const sortedFailedNotifications = useMemo(() => {
    if (!failedSort) return failedNotifications;
    const selector = (n: Notification) => (n as any)[failedSort.key];
    return sortBy(failedNotifications, selector, failedSort.dir);
  }, [failedNotifications, failedSort]);

  const filteredDeliveryLogs = useMemo(() => {
    const min =
      logsQuery.minDurationMs === "" ? null : Number(logsQuery.minDurationMs);
    const max =
      logsQuery.maxDurationMs === "" ? null : Number(logsQuery.maxDurationMs);
    return deliveryLogs.filter((l: any) => {
      // provider/status may already be server-filtered; keep client-side filter as safety
      if (
        logsQuery.provider &&
        String((l as any).provider) !== logsQuery.provider
      )
        return false;
      if (logsQuery.status && String((l as any).status) !== logsQuery.status)
        return false;
      const dur =
        (l as any).duration_ms ??
        (l as any).latency_ms ??
        (l as any).provider_response?.response_time_ms;
      const durNum = dur == null ? null : Number(dur);
      if (
        min != null &&
        Number.isFinite(min) &&
        durNum != null &&
        Number.isFinite(durNum) &&
        durNum < min
      )
        return false;
      if (
        max != null &&
        Number.isFinite(max) &&
        durNum != null &&
        Number.isFinite(durNum) &&
        durNum > max
      )
        return false;
      if (
        min != null &&
        Number.isFinite(min) &&
        (durNum == null || !Number.isFinite(durNum))
      )
        return false;
      if (
        max != null &&
        Number.isFinite(max) &&
        (durNum == null || !Number.isFinite(durNum))
      )
        return false;
      return true;
    });
  }, [
    deliveryLogs,
    logsQuery.provider,
    logsQuery.status,
    logsQuery.minDurationMs,
    logsQuery.maxDurationMs,
  ]);

  const sortedDeliveryLogs = useMemo(() => {
    if (!logsSort) return filteredDeliveryLogs;
    const selector = (l: any) => {
      if (logsSort.key === "duration_ms")
        return (l as any).duration_ms ?? (l as any).latency_ms;
      if (logsSort.key === "created_at")
        return (
          (l as any).created_at ||
          (l as any).attempted_at ||
          (l as any).timestamp
        );
      return (l as any)[logsSort.key];
    };
    return sortBy(filteredDeliveryLogs, selector, logsSort.dir);
  }, [filteredDeliveryLogs, logsSort]);

  const sortedEventTypes = useMemo(() => {
    if (!eventTypesSort) return eventTypes;
    const selector = (e: any) => (e as any)[eventTypesSort.key];
    return sortBy(eventTypes, selector, eventTypesSort.dir);
  }, [eventTypes, eventTypesSort]);

  const sortedSubscriptions = useMemo(() => {
    if (!subsSort) return subscriptions;
    const selector = (s: any) => (s as any)[subsSort.key];
    return sortBy(subscriptions, selector, subsSort.dir);
  }, [subscriptions, subsSort]);

  const failedPageData = useMemo(
    () => paginate(sortedFailedNotifications, failedPage, failedPageSize),
    [sortedFailedNotifications, failedPage, failedPageSize],
  );
  const logsPageData = useMemo(
    () => paginate(sortedDeliveryLogs, logsPage, logsPageSize),
    [sortedDeliveryLogs, logsPage, logsPageSize],
  );
  const eventTypesPageData = useMemo(
    () => paginate(sortedEventTypes, eventTypesPage, eventTypesPageSize),
    [sortedEventTypes, eventTypesPage, eventTypesPageSize],
  );
  const subsPageData = useMemo(
    () => paginate(sortedSubscriptions, subsPage, subsPageSize),
    [sortedSubscriptions, subsPage, subsPageSize],
  );

  useEffect(() => {
    // Only load if user has super admin access
    if (canAccess) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [canAccess]);

  // Auto-refresh delivery logs
  useEffect(() => {
    if (autoRefresh && activeTab === "logs") {
      const interval = setInterval(() => {
        loadDeliveryLogs();
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh, activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load dashboard data using ACTUAL available endpoints
      const [
        healthData,
        notificationStatsData,
        subscriptionStatsData,
        deliveryServicesData,
        pubsubData,
        circuitBreakersData,
      ] = await Promise.all([
        adminService.notificationService
          .getSystemHealthDetailed()
          .catch(() => null),
        adminService.notificationService.getSystemStats().catch(() => null),
        adminService.notificationService
          .getSubscriptionStats()
          .catch(() => null),
        adminService.notificationService
          .getDeliveryServicesStatus()
          .catch(() => ({})),
        adminService.notificationService.getPubSubStatus().catch(() => null),
        adminService.notificationService
          .getCircuitBreakersStatus()
          .catch(() => ({})),
      ]);

      // Set health status from REAL API response structure
      const overallStatus = healthData?.status || "unhealthy";
      const serviceChecks = healthData?.services || {};

      setHealth({
        status: overallStatus,
        message: healthData?.message || `System is ${overallStatus}`,
        timestamp: healthData?.timestamp || new Date().toISOString(),
        version: healthData?.version || "1.0.0",
        uptime_seconds: healthData?.uptime_seconds || 0,
        checks: {
          // ONLY show services that are ACTUALLY in the API response
          event_publisher: serviceChecks.event_publisher?.status === "healthy",
          subscription_manager:
            serviceChecks.subscription_manager?.status === "healthy",
          notification_dispatcher:
            serviceChecks.notification_dispatcher?.status === "healthy",
        },
      });

      // Set stats from REAL API responses
      // NotificationStatsResponse: {total_notifications, by_status, success_rate, failure_rate, average_delivery_time_ms}
      const totalNotifications =
        notificationStatsData?.total_notifications || 0;
      const successRate = normalizePercent(notificationStatsData?.success_rate);
      const byStatus = notificationStatsData?.by_status || {};
      const delivered = byStatus.delivered || byStatus.success || 0;
      const failed = byStatus.failed || 0;

      setStats({
        total_sent: totalNotifications,
        total_delivered: delivered,
        total_failed: failed,
        success_rate: successRate,
        avg_delivery_time: notificationStatsData?.average_delivery_time_ms || 0,
        active_subscriptions:
          subscriptionStatsData?.total_subscriptions ||
          subscriptionStatsData?.active_subscriptions ||
          0,
      });

      // Set system status from multiple sources
      setSystemStatus({
        pubsub_processor: {
          status: pubsubData?.is_running ? "running" : "stopped",
          message_count: pubsubData?.total_processed || 0,
          processing_rate: pubsubData?.processing_rate || 0,
          errors: pubsubData?.error_count || 0,
        },
        delivery_services: deliveryServicesData?.services || {
          email: { enabled: true, status: "unknown", success_rate: 0 },
          webhook: { enabled: true, status: "unknown", success_rate: 0 },
          firebase: { enabled: true, status: "unknown", success_rate: 0 },
        },
        circuit_breaker: circuitBreakersData || {
          email: "closed",
          webhook: "closed",
          firebase: "closed",
        },
      });

      // Recent events not available from a direct endpoint
      setRecentEvents([]);
    } catch (err) {
      console.error("Failed to load notification data:", err);
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadFailedNotifications = async () => {
    try {
      setNotificationsLoading(true);
      setNotificationsError(null);
      const notifications =
        await adminService.notificationService.listNotifications({
          status: "failed",
          skip: 0,
          limit: 100,
        });
      setFailedNotifications(notifications);
    } catch (err: any) {
      setNotificationsError(
        err.message || "Failed to load failed notifications",
      );
    } finally {
      setNotificationsLoading(false);
    }
  };

  const loadDeliveryLogs = async () => {
    try {
      setLogsLoading(true);
      setLogsError(null);
      const logs = await adminService.notificationService.getDeliveryLogs({
        hours: logsQuery.hours,
        limit: logsQuery.limit,
        provider: logsQuery.provider || undefined,
        status: logsQuery.status || undefined,
      });
      setDeliveryLogs(logs);
      setLogsPage(1);
    } catch (err: any) {
      setLogsError(err.message || "Failed to load delivery logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const loadEventTypes = async () => {
    try {
      setEventTypesLoading(true);
      setEventTypesError(null);
      const [types, categories] = await Promise.all([
        adminService.notificationService.listEventTypes({ active_only: true }),
        adminService.notificationService.getEventTypeCategories(),
      ]);
      setEventTypes(types);
      setEventTypeCategories(categories);
    } catch (err: any) {
      setEventTypesError(err.message || "Failed to load event types");
    } finally {
      setEventTypesLoading(false);
    }
  };

  const loadSubscriptions = async (
    view: "active" | "all" = subscriptionsView,
  ) => {
    try {
      setSubscriptionsLoading(true);
      setSubscriptionsError(null);
      const [subs, stats] = await Promise.all([
        adminService.notificationService.listAllSubscriptions({
          active_only: view === "active" ? true : undefined,
        }),
        adminService.notificationService.getSubscriptionStats(),
      ]);
      setSubscriptions(subs);
      setSubscriptionStats(stats);
    } catch (err: any) {
      setSubscriptionsError(err.message || "Failed to load subscriptions");
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const publishEvent = async () => {
    try {
      setPublishing(true);
      setPublishError(null);
      setPublishSuccess(null);

      const payload = JSON.parse(eventForm.payload);
      const metadata = JSON.parse(eventForm.metadata);

      const event = {
        event_type: eventForm.event_type,
        user_id: eventForm.user_id,
        priority: eventForm.priority,
        payload: { data: payload },
        metadata: metadata,
      };

      const result = await adminService.notificationService.publishEvent(event);
      setPublishSuccess(
        `Event published successfully! Correlation ID: ${result.correlation_id}`,
      );

      // Add to recent events
      setRecentEvents((prev) => [event, ...prev.slice(0, 9)]); // Keep last 10

      // Reset form
      setEventForm({
        event_type: "",
        user_id: "",
        priority: "normal",
        payload: "{}",
        metadata: "{}",
      });
    } catch (err: any) {
      setPublishError(err.message || "Failed to publish event");
    } finally {
      setPublishing(false);
    }
  };

  const handleRetryNotification = async (notification: Notification) => {
    setSelectedNotification(notification);
    setRetryModalOpen(true);
  };

  const openDetails = (title: string, payload: any) => {
    setDetailsTitle(title);
    setDetailsPayload(payload);
    setDetailsModalOpen(true);
  };

  const confirmRetry = async () => {
    if (!selectedNotification) return;
    if (!selectedNotification.id) {
      setNotificationsError(
        "Cannot retry: notification ID is missing from the API response.",
      );
      return;
    }

    try {
      await adminService.notificationService.retryNotification(
        selectedNotification.id,
        retryConfig,
      );
      setRetryModalOpen(false);
      setSelectedNotification(null);
      // Reload failed notifications
      await loadFailedNotifications();
    } catch (err: any) {
      setNotificationsError(err.message || "Failed to retry notification");
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);

    // Load data based on tab
    switch (tab) {
      case "analytics":
        loadFailedNotifications();
        break;
      case "logs":
        loadDeliveryLogs();
        break;
      case "admin":
        loadEventTypes();
        break;
      case "subscriptions":
        loadSubscriptions();
        break;
      case "events":
        // Event publisher - load event types for dropdown
        loadEventTypes();
        break;
    }
  };

  const getHealthIcon = (status?: string) => {
    switch (status) {
      case "healthy":
        return CheckCircle;
      case "degraded":
        return AlertCircle;
      case "unhealthy":
        return AlertCircle;
      default:
        return Clock;
    }
  };

  // Show 403 if not super admin
  if (!canAccess) {
    return (
      <div className="-mt-20 flex min-h-screen items-center justify-center">
        <Card className="max-w-md border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody className="py-12 text-center">
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <ShieldAlert className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="mb-2 text-2xl font-bold text-red-900 dark:text-red-100">
              Admin Access Required
            </h3>
            <p className="mb-2 text-red-700 dark:text-red-300">
              Only organization administrators can access the notification
              service management.
            </p>
            <p className="mb-6 text-sm text-red-600 dark:text-red-400">
              This page manages the external notification service and requires
              elevated privileges.
            </p>
            <button
              onClick={() => navigate({ to: "/admin" })}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-red-600 hover:to-red-700"
            >
              Go to Dashboard
            </button>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="mb-2 flex items-center gap-3 text-4xl font-bold">
              <Bell className="h-10 w-10" />
              Atlas Notification Service
            </h1>
            <p className="text-lg text-orange-100">
              Manage enterprise-grade notifications, subscriptions, and
              real-time messaging
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm sm:block">
              <p className="text-sm text-orange-100">Service Status</p>
              <div className="flex items-center gap-2">
                {health && (
                  <>
                    {health.status === "healthy" && (
                      <CheckCircle className="h-5 w-5 text-green-300" />
                    )}
                    {health.status === "degraded" && (
                      <AlertCircle className="h-5 w-5 text-yellow-300" />
                    )}
                    {health.status === "unhealthy" && (
                      <AlertCircle className="h-5 w-5 text-red-300" />
                    )}
                    <span className="text-lg font-bold capitalize">
                      {health.status || "Unknown"}
                    </span>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={loadData}
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-4 -right-12 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody>
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Connection Error
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Tab Navigation */}
      <Card>
        <CardBody>
          <div className="flex flex-wrap gap-2">
            <TabButton
              id="dashboard"
              label="Health Dashboard"
              icon={Activity}
              isActive={activeTab === "dashboard"}
              onClick={() => handleTabChange("dashboard")}
            />
            <TabButton
              id="events"
              label="Event Publisher"
              icon={Send}
              isActive={activeTab === "events"}
              onClick={() => handleTabChange("events")}
            />
            <TabButton
              id="subscriptions"
              label="Subscriptions"
              icon={Users}
              isActive={activeTab === "subscriptions"}
              onClick={() => handleTabChange("subscriptions")}
            />
            <TabButton
              id="analytics"
              label="Failed Notifications"
              icon={BarChart3}
              isActive={activeTab === "analytics"}
              onClick={() => handleTabChange("analytics")}
            />
            <TabButton
              id="logs"
              label="Delivery Logs"
              icon={Activity}
              isActive={activeTab === "logs"}
              onClick={() => handleTabChange("logs")}
            />
            <TabButton
              id="admin"
              label="Event Types"
              icon={Settings}
              isActive={activeTab === "admin"}
              onClick={() => handleTabChange("admin")}
            />
          </div>
        </CardBody>
      </Card>

      {/* Health Dashboard Tab */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {/* System Health Overview */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="System Status"
              value={health?.status?.toUpperCase() || "UNKNOWN"}
              trend={
                health?.timestamp
                  ? {
                      value: new Date(health.timestamp).toLocaleTimeString(),
                      isPositive: health?.status === "healthy",
                      label: "last updated",
                    }
                  : undefined
              }
              icon={getHealthIcon(health?.status)}
              gradient="from-green-400 to-emerald-600"
            />
            <StatCard
              title="Active Subscriptions"
              value={stats?.active_subscriptions?.toString() || "0"}
              icon={Bell}
              gradient="from-blue-400 to-blue-600"
            />
            <StatCard
              title="Total Notifications"
              value={stats?.total_sent?.toString() || "0"}
              trend={
                stats?.total_delivered
                  ? {
                      value: `${stats.total_delivered} delivered`,
                      isPositive: true,
                      label: "",
                    }
                  : undefined
              }
              icon={Send}
              gradient="from-purple-400 to-purple-600"
            />
            <StatCard
              title="Success Rate"
              value={`${(stats?.success_rate || 0).toFixed(1)}%`}
              trend={
                stats?.success_rate
                  ? {
                      value:
                        stats.success_rate >= 95
                          ? "Excellent"
                          : "Needs attention",
                      isPositive: stats.success_rate >= 95,
                      label: "",
                    }
                  : undefined
              }
              icon={CheckCircle}
              gradient="from-orange-400 to-red-600"
            />
          </div>

          {/* Service Health Checks */}
          {health?.checks && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Service Health Checks
                </h3>
              </CardHeader>
              <CardBody>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(health.checks).map(([service, status]) => {
                    const isHealthy = status === true;
                    const serviceName = service
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase());
                    return (
                      <div
                        key={service}
                        className="flex items-center justify-between rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 p-4 dark:from-gray-800 dark:to-gray-700"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`rounded-lg p-2 ${isHealthy ? "bg-green-100 dark:bg-green-900/30" : "bg-red-100 dark:bg-red-900/30"}`}
                          >
                            <Server
                              className={`h-4 w-4 ${isHealthy ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                            />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {serviceName}
                            </p>
                            <p
                              className={`text-sm ${isHealthy ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                            >
                              {isHealthy ? "Healthy" : "Unhealthy"}
                            </p>
                          </div>
                        </div>
                        {isHealthy ? (
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Delivery Channels Status */}
          {systemStatus?.delivery_services && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delivery Channels
                </h3>
              </CardHeader>
              <CardBody>
                <div className="grid gap-4 md:grid-cols-3">
                  {Object.entries(systemStatus.delivery_services).map(
                    ([channel, service]: [string, any]) => {
                      // Use ONLY real data from API - no assumptions
                      const channelConfig = {
                        email: { icon: Mail, color: "blue" },
                        webhook: { icon: Webhook, color: "purple" },
                        firebase: { icon: Smartphone, color: "orange" },
                      }[channel] || { icon: Server, color: "gray" };

                      const Icon = channelConfig.icon;
                      const isEnabled = service.enabled !== false;
                      // Capitalize channel name for display
                      const displayName =
                        channel.charAt(0).toUpperCase() + channel.slice(1);

                      return (
                        <div
                          key={channel}
                          className={`rounded-xl bg-gradient-to-br p-6 from-${channelConfig.color}-50 to-${channelConfig.color}-100 border border-${channelConfig.color}-200`}
                        >
                          <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div
                                className={`rounded-lg p-2 bg-${channelConfig.color}-100`}
                              >
                                <Icon
                                  className={`h-5 w-5 text-${channelConfig.color}-600`}
                                />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {displayName}
                                </p>
                              </div>
                            </div>
                            <div
                              className={`rounded-full px-2 py-1 text-xs font-medium ${
                                service.status === "healthy"
                                  ? "bg-green-100 text-green-800"
                                  : service.status === "degraded"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                              }`}
                            >
                              {service.status}
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {isEnabled ? "Enabled" : "Disabled"}
                          </div>
                          <p className="text-sm text-gray-600">
                            {service.message}
                          </p>
                        </div>
                      );
                    },
                  )}
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* Failed Notifications Tab */}
      {activeTab === "analytics" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Failed Notifications
                </h3>
                <button
                  onClick={loadFailedNotifications}
                  disabled={notificationsLoading}
                  className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${notificationsLoading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button>
              </div>
            </CardHeader>
            <CardBody>
              {notificationsError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {notificationsError}
                    </p>
                  </div>
                </div>
              )}

              {notificationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-orange-600"></div>
                </div>
              ) : failedNotifications.length === 0 ? (
                <div className="py-12 text-center">
                  <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />
                  <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                    No Failed Notifications
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    All notifications are being delivered successfully!
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setFailedSort((prev) => {
                              const key = "id" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Notification ID
                          {getSortIndicator(
                            failedSort?.key === "id",
                            failedSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setFailedSort((prev) => {
                              const key = "event_type" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Event Type
                          {getSortIndicator(
                            failedSort?.key === "event_type",
                            failedSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setFailedSort((prev) => {
                              const key = "delivery_channel" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Channel
                          {getSortIndicator(
                            failedSort?.key === "delivery_channel",
                            failedSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setFailedSort((prev) => {
                              const key = "user_id" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          User ID
                          {getSortIndicator(
                            failedSort?.key === "user_id",
                            failedSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setFailedSort((prev) => {
                              const key = "created_at" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "desc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Failed At
                          {getSortIndicator(
                            failedSort?.key === "created_at",
                            failedSort?.dir,
                          )}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Error Message
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                      {failedPageData.rows.map((notification, idx) => (
                        <tr
                          key={
                            notification.id ||
                            notification.event_id ||
                            notification.subscription_id ||
                            `notif-${idx}`
                          }
                          className="hover:bg-gray-50 dark:hover:bg-gray-800"
                          onClick={() =>
                            openDetails(
                              "Failed Notification Details",
                              notification,
                            )
                          }
                          role="button"
                          tabIndex={0}
                        >
                          <td className="whitespace-nowrap px-6 py-4 font-mono text-sm text-gray-900 dark:text-white">
                            {formatShortId(notification.id)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {notification.event_type || "—"}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                              {notification.delivery_channel || "—"}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {formatShortId(notification.user_id)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {formatDateTime(notification.created_at)}
                          </td>
                          <td className="max-w-xs truncate px-6 py-4 text-sm text-red-600 dark:text-red-400">
                            {notification.error_message || "Unknown error"}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRetryNotification(notification);
                              }}
                              className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                            >
                              Retry
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {failedNotifications.length > 0 && (
                <PaginationControls
                  page={failedPageData.page}
                  totalPages={failedPageData.totalPages}
                  total={failedPageData.total}
                  start={failedPageData.start}
                  end={failedPageData.end}
                  pageSize={failedPageData.pageSize}
                  onPageChange={setFailedPage}
                  onPageSizeChange={(size) => {
                    setFailedPageSize(size);
                    setFailedPage(1);
                  }}
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Delivery Logs Tab */}
      {activeTab === "logs" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delivery Logs
                </h3>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    Auto-refresh
                  </label>
                  <button
                    onClick={loadDeliveryLogs}
                    disabled={logsLoading}
                    className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${logsLoading ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {/* Filters */}
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-800/30">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-6">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Provider
                    </label>
                    <input
                      value={logsQuery.provider}
                      onChange={(e) =>
                        setLogsQuery((prev) => ({
                          ...prev,
                          provider: e.target.value,
                        }))
                      }
                      placeholder="e.g. webhook"
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Status
                    </label>
                    <input
                      value={logsQuery.status}
                      onChange={(e) =>
                        setLogsQuery((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      placeholder="success|failed|retrying"
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Hours
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={logsQuery.hours}
                      onChange={(e) =>
                        setLogsQuery((prev) => ({
                          ...prev,
                          hours: Math.max(
                            1,
                            parseInt(e.target.value || "24", 10),
                          ),
                        }))
                      }
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Fetch limit
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={logsQuery.limit}
                      onChange={(e) =>
                        setLogsQuery((prev) => ({
                          ...prev,
                          limit: Math.max(
                            1,
                            parseInt(e.target.value || "100", 10),
                          ),
                        }))
                      }
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Min duration (ms)
                    </label>
                    <input
                      value={logsQuery.minDurationMs}
                      onChange={(e) =>
                        setLogsQuery((prev) => ({
                          ...prev,
                          minDurationMs: e.target.value,
                        }))
                      }
                      placeholder="e.g. 50"
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Max duration (ms)
                    </label>
                    <input
                      value={logsQuery.maxDurationMs}
                      onChange={(e) =>
                        setLogsQuery((prev) => ({
                          ...prev,
                          maxDurationMs: e.target.value,
                        }))
                      }
                      placeholder="e.g. 5000"
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    onClick={() => {
                      setLogsQuery({
                        hours: 24,
                        limit: 100,
                        provider: "",
                        status: "",
                        minDurationMs: "",
                        maxDurationMs: "",
                      });
                      setLogsPage(1);
                    }}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  >
                    Reset
                  </button>
                  <button
                    onClick={loadDeliveryLogs}
                    disabled={logsLoading}
                    className="rounded-lg bg-orange-600 px-3 py-2 text-sm text-white hover:bg-orange-700 disabled:opacity-50"
                  >
                    Apply
                  </button>
                </div>
              </div>

              {logsError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {logsError}
                    </p>
                  </div>
                </div>
              )}

              {logsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-orange-600"></div>
                </div>
              ) : logsPageData.total === 0 ? (
                <div className="py-12 text-center">
                  <Activity className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                  <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                    No Delivery Logs
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    No delivery logs found for the last 24 hours.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setLogsSort((prev) => {
                              const key = "created_at" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "desc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Timestamp
                          {getSortIndicator(
                            logsSort?.key === "created_at",
                            logsSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setLogsSort((prev) => {
                              const key = "notification_id" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Notification ID
                          {getSortIndicator(
                            logsSort?.key === "notification_id",
                            logsSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setLogsSort((prev) => {
                              const key = "provider" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Provider
                          {getSortIndicator(
                            logsSort?.key === "provider",
                            logsSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setLogsSort((prev) => {
                              const key = "status" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Status
                          {getSortIndicator(
                            logsSort?.key === "status",
                            logsSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setLogsSort((prev) => {
                              const key = "duration_ms" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "desc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Duration
                          {getSortIndicator(
                            logsSort?.key === "duration_ms",
                            logsSort?.dir,
                          )}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Response
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                      {logsPageData.rows.map((log) => (
                        <tr
                          key={log.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800"
                          onClick={() =>
                            openDetails("Delivery Log Details", log)
                          }
                          role="button"
                          tabIndex={0}
                        >
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {formatDateTime(
                              (log as any).created_at ||
                                (log as any).attempted_at ||
                                (log as any).timestamp,
                            )}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 font-mono text-sm text-gray-900 dark:text-white">
                            {formatShortId(log.notification_id)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                              {log.provider}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                log.status === "success"
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                              }`}
                            >
                              {log.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {formatDurationMs(
                              (log as any).duration_ms ??
                                (log as any).latency_ms ??
                                (log as any).provider_response
                                  ?.response_time_ms,
                            )}
                          </td>
                          <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {(log as any).response_message ||
                              (log as any).error_message ||
                              "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {logsPageData.total > 0 && (
                <PaginationControls
                  page={logsPageData.page}
                  totalPages={logsPageData.totalPages}
                  total={logsPageData.total}
                  start={logsPageData.start}
                  end={logsPageData.end}
                  pageSize={logsPageData.pageSize}
                  onPageChange={setLogsPage}
                  onPageSizeChange={(size) => {
                    setLogsPageSize(size);
                    setLogsPage(1);
                  }}
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Event Types Management Tab */}
      {activeTab === "admin" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Event Types Management
                </h3>
                <button
                  onClick={() => {
                    setEditingEventType(null);
                    setEventTypeModalOpen(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
                >
                  <Settings className="h-4 w-4" />
                  Create Event Type
                </button>
              </div>
            </CardHeader>
            <CardBody>
              {eventTypesError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {eventTypesError}
                    </p>
                  </div>
                </div>
              )}

              {eventTypesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-orange-600"></div>
                </div>
              ) : eventTypes.length === 0 ? (
                <div className="py-12 text-center">
                  <Settings className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                  <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                    No Event Types
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Create your first event type to get started.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setEventTypesSort((prev) => {
                              const key = "name" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Name
                          {getSortIndicator(
                            eventTypesSort?.key === "name",
                            eventTypesSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setEventTypesSort((prev) => {
                              const key = "display_name" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Display Name
                          {getSortIndicator(
                            eventTypesSort?.key === "display_name",
                            eventTypesSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setEventTypesSort((prev) => {
                              const key = "category" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Category
                          {getSortIndicator(
                            eventTypesSort?.key === "category",
                            eventTypesSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setEventTypesSort((prev) => {
                              const key = "is_active" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "desc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Status
                          {getSortIndicator(
                            eventTypesSort?.key === "is_active",
                            eventTypesSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setEventTypesSort((prev) => {
                              const key = "schema_version" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Schema Version
                          {getSortIndicator(
                            eventTypesSort?.key === "schema_version",
                            eventTypesSort?.dir,
                          )}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                      {eventTypesPageData.rows.map((eventType) => (
                        <tr
                          key={eventType.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <td className="whitespace-nowrap px-6 py-4 font-mono text-sm text-gray-900 dark:text-white">
                            {eventType.name}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {eventType.display_name}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                              {eventType.category}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                eventType.is_active
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                              }`}
                            >
                              {eventType.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {eventType.schema_version}
                          </td>
                          <td className="space-x-2 whitespace-nowrap px-6 py-4 text-sm font-medium">
                            <button
                              onClick={() => {
                                setEditingEventType(eventType);
                                setEventTypeModalOpen(true);
                              }}
                              className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                            >
                              Edit
                            </button>
                            <button
                              onClick={async () => {
                                if (
                                  confirm(
                                    "Are you sure you want to delete this event type?",
                                  )
                                ) {
                                  try {
                                    await adminService.notificationService.deleteEventType(
                                      eventType.id,
                                    );
                                    await loadEventTypes();
                                  } catch (err: any) {
                                    setEventTypesError(
                                      err.message ||
                                        "Failed to delete event type",
                                    );
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {eventTypes.length > 0 && (
                <PaginationControls
                  page={eventTypesPageData.page}
                  totalPages={eventTypesPageData.totalPages}
                  total={eventTypesPageData.total}
                  start={eventTypesPageData.start}
                  end={eventTypesPageData.end}
                  pageSize={eventTypesPageData.pageSize}
                  onPageChange={setEventTypesPage}
                  onPageSizeChange={(size) => {
                    setEventTypesPageSize(size);
                    setEventTypesPage(1);
                  }}
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Subscriptions Management Tab */}
      {activeTab === "subscriptions" && (
        <div className="space-y-6">
          {/* Stats Summary */}
          {subscriptionStats && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Total Subscriptions"
                value={subscriptionStats.total_subscriptions.toString()}
                icon={Users}
                gradient="from-blue-400 to-blue-600"
              />
              <StatCard
                title="Active Subscriptions"
                value={subscriptionStats.active_subscriptions.toString()}
                icon={CheckCircle}
                gradient="from-green-400 to-emerald-600"
              />
              <StatCard
                title="Email Channel"
                value={(subscriptionStats?.by_channel?.email ?? 0).toString()}
                icon={Mail}
                gradient="from-purple-400 to-purple-600"
              />
              <StatCard
                title="Webhook Channel"
                value={(subscriptionStats?.by_channel?.webhook ?? 0).toString()}
                icon={Webhook}
                gradient="from-orange-400 to-red-600"
              />
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  All Subscriptions
                </h3>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Show
                  </label>
                  <select
                    value={subscriptionsView}
                    onChange={(e) => {
                      const view = e.target.value as "active" | "all";
                      setSubscriptionsView(view);
                      loadSubscriptions(view);
                    }}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="active">Active only</option>
                    <option value="all">All</option>
                  </select>
                  <button
                    onClick={() => loadSubscriptions()}
                    disabled={subscriptionsLoading}
                    className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${subscriptionsLoading ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {subscriptionsError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {subscriptionsError}
                    </p>
                  </div>
                </div>
              )}

              {subscriptionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-orange-600"></div>
                </div>
              ) : subscriptions.length === 0 ? (
                <div className="py-12 text-center">
                  <Users className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                  <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                    No Subscriptions
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    No subscriptions found.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setSubsSort((prev) => {
                              const key = "id" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Subscription ID
                          {getSortIndicator(
                            subsSort?.key === "id",
                            subsSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setSubsSort((prev) => {
                              const key = "user_id" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          User ID
                          {getSortIndicator(
                            subsSort?.key === "user_id",
                            subsSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setSubsSort((prev) => {
                              const key = "event_type" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Event Type
                          {getSortIndicator(
                            subsSort?.key === "event_type",
                            subsSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setSubsSort((prev) => {
                              const key = "delivery_channel" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "asc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Channel
                          {getSortIndicator(
                            subsSort?.key === "delivery_channel",
                            subsSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setSubsSort((prev) => {
                              const key = "is_active" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "desc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Status
                          {getSortIndicator(
                            subsSort?.key === "is_active",
                            subsSort?.dir,
                          )}
                        </th>
                        <th
                          className="cursor-pointer select-none px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          onClick={() =>
                            setSubsSort((prev) => {
                              const key = "created_at" as const;
                              if (!prev || prev.key !== key)
                                return { key, dir: "desc" };
                              return {
                                key,
                                dir: prev.dir === "asc" ? "desc" : "asc",
                              };
                            })
                          }
                        >
                          Created
                          {getSortIndicator(
                            subsSort?.key === "created_at",
                            subsSort?.dir,
                          )}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                      {subsPageData.rows.map((subscription) => (
                        <tr
                          key={subscription.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <td className="whitespace-nowrap px-6 py-4 font-mono text-sm text-gray-900 dark:text-white">
                            {formatShortId(subscription.id)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {formatShortId(subscription.user_id)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {subscription.event_type}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                              {subscription.delivery_channel}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm">
                            <div className="flex flex-col gap-1">
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                  subscription.is_active
                                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                                }`}
                              >
                                {subscription.is_active ? "Active" : "Inactive"}
                              </span>
                              {subscription.is_paused && (
                                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                  Paused
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {formatDateTime(subscription.created_at)}
                          </td>
                          <td className="space-x-2 whitespace-nowrap px-6 py-4 text-sm font-medium">
                            <button
                              onClick={async () => {
                                try {
                                  await adminService.notificationService.updateSubscriptionById(
                                    subscription.id,
                                    {
                                      is_active: !subscription.is_active,
                                    },
                                  );
                                  await loadSubscriptions();
                                } catch (err: any) {
                                  setSubscriptionsError(
                                    err.message ||
                                      "Failed to toggle subscription",
                                  );
                                }
                              }}
                              className={`text-sm ${
                                subscription.is_active
                                  ? "text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  : "text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              }`}
                            >
                              {subscription.is_active ? "Disable" : "Enable"}
                            </button>
                            <button
                              onClick={async () => {
                                if (
                                  confirm(
                                    "Are you sure you want to delete this subscription?",
                                  )
                                ) {
                                  try {
                                    await adminService.notificationService.deleteSubscriptionById(
                                      subscription.id,
                                    );
                                    await loadSubscriptions();
                                  } catch (err: any) {
                                    setSubscriptionsError(
                                      err.message ||
                                        "Failed to delete subscription",
                                    );
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {subscriptions.length > 0 && (
                <PaginationControls
                  page={subsPageData.page}
                  totalPages={subsPageData.totalPages}
                  total={subsPageData.total}
                  start={subsPageData.start}
                  end={subsPageData.end}
                  pageSize={subsPageData.pageSize}
                  onPageChange={setSubsPage}
                  onPageSizeChange={(size) => {
                    setSubsPageSize(size);
                    setSubsPage(1);
                  }}
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Event Publisher Tab */}
      {activeTab === "events" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Event Publisher
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Publish events to the notification service with custom payloads
                and priorities.
              </p>
            </CardHeader>
            <CardBody>
              {publishError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {publishError}
                    </p>
                  </div>
                </div>
              )}

              {publishSuccess && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <p className="text-sm text-green-800 dark:text-green-200">
                      {publishSuccess}
                    </p>
                  </div>
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  publishEvent();
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Event Type *
                    </label>
                    <select
                      required
                      value={eventForm.event_type}
                      onChange={(e) =>
                        setEventForm((prev) => ({
                          ...prev,
                          event_type: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Select event type</option>
                      {eventTypes.map((eventType) => (
                        <option key={eventType.id} value={eventType.name}>
                          {eventType.display_name} ({eventType.name})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      User ID *
                    </label>
                    <input
                      type="text"
                      required
                      value={eventForm.user_id}
                      onChange={(e) =>
                        setEventForm((prev) => ({
                          ...prev,
                          user_id: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500"
                      placeholder="user-123"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Priority
                  </label>
                  <select
                    value={eventForm.priority}
                    onChange={(e) =>
                      setEventForm((prev) => ({
                        ...prev,
                        priority: e.target.value as any,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Payload (JSON) *
                  </label>
                  <textarea
                    required
                    value={eventForm.payload}
                    onChange={(e) =>
                      setEventForm((prev) => ({
                        ...prev,
                        payload: e.target.value,
                      }))
                    }
                    rows={6}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 font-mono text-sm text-gray-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500"
                    placeholder='{"message": "Hello World", "data": {...}}'
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Metadata (JSON)
                  </label>
                  <textarea
                    value={eventForm.metadata}
                    onChange={(e) =>
                      setEventForm((prev) => ({
                        ...prev,
                        metadata: e.target.value,
                      }))
                    }
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 font-mono text-sm text-gray-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500"
                    placeholder='{"source": "admin", "version": "1.0"}'
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={publishing}
                    className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-6 py-3 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                  >
                    {publishing ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                        Publishing...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Publish Event
                      </>
                    )}
                  </button>
                </div>
              </form>
            </CardBody>
          </Card>

          {/* Recent Events */}
          {recentEvents.length > 0 && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Recent Published Events
                </h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {recentEvents.map((event, index) => (
                    <div
                      key={index}
                      className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {event.event_type}
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            event.priority === "critical"
                              ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                              : event.priority === "high"
                                ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                : event.priority === "normal"
                                  ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                          }`}
                        >
                          {event.priority}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        User: {event.user_id}
                      </div>
                      <div className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-500">
                        {JSON.stringify(
                          event.payload?.data || event.payload,
                          null,
                          2,
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* Retry Modal */}
      {retryModalOpen && selectedNotification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Retry Notification
            </h3>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                  Notification ID:{" "}
                  <span className="font-mono">
                    {formatShortId(selectedNotification.id)}
                  </span>
                </p>
                <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                  Event Type:{" "}
                  <span className="font-medium">
                    {selectedNotification.event_type}
                  </span>
                </p>
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Channel:{" "}
                  <span className="font-medium">
                    {selectedNotification.delivery_channel}
                  </span>
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Max Attempts
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={retryConfig.max_attempts}
                  onChange={(e) =>
                    setRetryConfig((prev) => ({
                      ...prev,
                      max_attempts: parseInt(e.target.value) || 1,
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Delay (seconds)
                </label>
                <input
                  type="number"
                  min="0"
                  max="300"
                  value={retryConfig.delay_seconds}
                  onChange={(e) =>
                    setRetryConfig((prev) => ({
                      ...prev,
                      delay_seconds: parseInt(e.target.value) || 0,
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setRetryModalOpen(false);
                  setSelectedNotification(null);
                }}
                className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmRetry}
                className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
              >
                Retry Notification
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {detailsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 max-h-[80vh] w-full max-w-3xl overflow-auto rounded-lg bg-white p-6 dark:bg-gray-800">
            <div className="mb-4 flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {detailsTitle}
              </h3>
              <button
                onClick={() => setDetailsModalOpen(false)}
                className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
            <pre className="whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-4 font-mono text-xs text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
              {JSON.stringify(detailsPayload, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Event Type Modal */}
      {eventTypeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {editingEventType ? "Edit Event Type" : "Create Event Type"}
            </h3>
            <EventTypeForm
              eventType={editingEventType}
              categories={eventTypeCategories}
              onSave={async (data) => {
                try {
                  if (editingEventType) {
                    await adminService.notificationService.updateEventType(
                      editingEventType.id,
                      data,
                    );
                  } else {
                    await adminService.notificationService.createEventType(
                      data,
                    );
                  }
                  setEventTypeModalOpen(false);
                  setEditingEventType(null);
                  await loadEventTypes();
                } catch (err: any) {
                  setEventTypesError(
                    err.message || "Failed to save event type",
                  );
                }
              }}
              onCancel={() => {
                setEventTypeModalOpen(false);
                setEditingEventType(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Event Type Form Component
function EventTypeForm({
  eventType,
  categories,
  onSave,
  onCancel,
}: {
  eventType: EventType | null;
  categories: string[];
  onSave: (data: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: eventType?.name || "",
    display_name: eventType?.display_name || "",
    description: eventType?.description || "",
    category: eventType?.category || "",
    schema_version: eventType?.schema_version || "1.0.0",
    payload_schema: eventType?.payload_schema || {},
    is_active: eventType?.is_active ?? true,
  });

  const [schemaJson, setSchemaJson] = useState(
    JSON.stringify(formData.payload_schema, null, 2),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload_schema = JSON.parse(schemaJson);
      onSave({ ...formData, payload_schema });
    } catch (err) {
      alert("Invalid JSON in payload schema");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Name *
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            placeholder="user.registered"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Display Name *
          </label>
          <input
            type="text"
            required
            value={formData.display_name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, display_name: e.target.value }))
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            placeholder="User Registered"
          />
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, description: e.target.value }))
          }
          rows={3}
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          placeholder="Description of this event type"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Category *
          </label>
          <select
            required
            value={formData.category}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, category: e.target.value }))
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Select category</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Schema Version
          </label>
          <input
            type="text"
            value={formData.schema_version}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                schema_version: e.target.value,
              }))
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            placeholder="1.0.0"
          />
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Payload Schema (JSON)
        </label>
        <textarea
          value={schemaJson}
          onChange={(e) => setSchemaJson(e.target.value)}
          rows={8}
          className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          placeholder='{"type": "object", "properties": {...}}'
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
          }
          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
        />
        <label
          htmlFor="is_active"
          className="ml-2 text-sm text-gray-700 dark:text-gray-300"
        >
          Active
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
        >
          {eventType ? "Update" : "Create"} Event Type
        </button>
      </div>
    </form>
  );
}
